import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { SubmitHandler, useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";

import Button from "../../components/Button";
import AppLogo from "../../components/AppLogo/AppLogo";
import FormItem from "../../components/FormItem";
import InputErrorMessage from "../../components/InputErrorMessage";
import InputField from "../../components/InputField";

import { generateRequestConfig } from "../../constants";
import { AuthContext } from "../../contexts/AuthContext";
import useTimer from "../../hooks/useTimer";
import { getAppInfo, handleErrorForms, getIdApp, ensureCsrfToken } from "../../utils";

import RegisterSkeleton from "../Skeletons/RegisterSkeleton";
import {
  registrationFields,
  getEmailErrorMessages,
  getPasswordErrorMessages,
  getPasswordRepeatErrorMessages,
  RegistrationFormDataType,
} from "./Registration.constants";

type RegistrationState = {
  generalErrors?: string;
  submitButton: boolean;
  appInfo: any;
};

const Registration: React.FC = () => {
  type FormField = "email" | "password" | "passwordRepeat";

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  const { AuthContextState } = useContext(AuthContext);
  const { csrf_token, idApp, referer } = AuthContextState;

  const [state, setState] = useState<RegistrationState>({
    appInfo: null,
    generalErrors: undefined,
    submitButton: true,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    trigger,
    setError,
    clearErrors,
  } = useForm<RegistrationFormDataType>();

  const isLoaded = useTimer(600);

  useEffect(() => {
    document.title = t("Registration");

    if (!getIdApp()) {
      navigate("/");
    }

    if (!state.appInfo) {
      getAppInfo((data) =>
        setState((prev) => ({ ...prev, appInfo: data }))
      );
    }
  }, [t, i18n, navigate, state.appInfo]);

  const handleSubmitRegistration: SubmitHandler<RegistrationFormDataType> = async (data) => {
    const csrfToken = await ensureCsrfToken(csrf_token);
    setState((prev) => ({ ...prev, submitButton: true, generalErrors: undefined }));

    const requestConfig = generateRequestConfig(
      "post",
      "registration",
      { ...data, appId: idApp },
      {
        "X-CSRF-TOKEN": csrfToken,
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "Accept-Language": currentLang,
      }
    );

    try {
      const res = await axios(requestConfig);

      if (res.status === 200) {
        const encodedEmail = encodeURIComponent(data.email);

        navigate(
          `/${currentLang}/activation/?appId=${idApp}&referer=${referer}&email=${encodedEmail}`
        );
      }
    } catch (error) {
      const errorAction = handleErrorForms(error);

      if (errorAction) {
        const { message, field } = errorAction;

        if (field) {
          setError(field as FormField, {
            type: "server",
            message,
          });
        } else {
          setState((prev) => ({ ...prev, generalErrors: message }));
        }
      }

      setState((prev) => ({ ...prev, submitButton: false }));
    }
  };

  const emailErrorMessages = getEmailErrorMessages(errors);
  const passwordErrorMessages = getPasswordErrorMessages(errors);
  const passwordRepeatErrorMessages = getPasswordRepeatErrorMessages(errors);

  const { appInfo, generalErrors, submitButton } = state;

  return (
    <>
      {!isLoaded ? (
        <RegisterSkeleton />
      ) : (
        <>
          <h1 className="text-center auth-title-form">
            {t("Registration")}
            <AppLogo
              src={appInfo?.urlImage}
              name={appInfo?.name}
              frameClassName="auth-title-logo-frame"
              imageClassName="auth-title-logo"
              placeholderClassName="auth-title-logo-placeholder"
            />
          </h1>

          <div className="card-body">
            <form noValidate onSubmit={handleSubmit(handleSubmitRegistration)}>

              <FormItem label={t(registrationFields.email.label)} labelFor={registrationFields.email.id}>
                <InputField
                  autoFocus
                  id={registrationFields.email.id}
                  type={registrationFields.email.type}
                  placeholder={t(registrationFields.email.placeholder)}
                  {...register(registrationFields.email.name, registrationFields.email.validation)}
                  className={generalErrors || errors.email ? "error" : undefined}
                />
                {errors.email && (
                  <InputErrorMessage msg={t(emailErrorMessages[errors.email.type])} />
                )}
              </FormItem>

              <FormItem label={t(registrationFields.password.label)} labelFor={registrationFields.password.id}>
                <InputField
                  title={t(
                    "at least 8 characters, both uppercase and lowercase letters, at least one numeric digit, and one special character"
                  )}
                  id={registrationFields.password.id}
                  type={registrationFields.password.type}
                  placeholder={t(registrationFields.password.placeholder)}
                  iconName={registrationFields.password.iconName}
                  {...register(registrationFields.password.name, {
                    ...registrationFields.password.validation,
                    validate: () => true,
                    onChange: () => {
                      clearErrors("passwordRepeat");
                      trigger(registrationFields.password.name);
                      trigger(registrationFields.passwordRepeat.name);
                    },
                  })}
                  className={generalErrors || errors.password ? "error" : undefined}
                />
                {errors.password && (
                  <InputErrorMessage msg={t(passwordErrorMessages[errors.password.type])} />
                )}
              </FormItem>

              <FormItem label={t(registrationFields.passwordRepeat.label)} labelFor={registrationFields.passwordRepeat.id}>
                <InputField
                  id={registrationFields.passwordRepeat.id}
                  type={registrationFields.passwordRepeat.type}
                  placeholder={t(registrationFields.passwordRepeat.placeholder)}
                  iconName={registrationFields.passwordRepeat.iconName}
                  {...register(registrationFields.passwordRepeat.name, {
                    ...registrationFields.passwordRepeat.validation,
                    validate: (value) =>
                      value === watch("password")
                        ? (clearErrors("passwordRepeat"), true)
                        : t("Passwords do not match. Please try again."),
                    onChange: () => {
                      clearErrors("passwordRepeat");
                      trigger(registrationFields.password.name);
                      trigger(registrationFields.passwordRepeat.name);
                    },
                  })}
                  className={generalErrors || errors.passwordRepeat ? "error" : undefined}
                />
                {errors.passwordRepeat && (
                  <InputErrorMessage msg={t(passwordRepeatErrorMessages[errors.passwordRepeat.type])} />
                )}
                {generalErrors && <InputErrorMessage msg={t(generalErrors)} />}
              </FormItem>

              <FormItem>
                <div>
                  <label htmlFor="agree">
                  <input
                    id="agree"
                    type="checkbox"
                    {...register("agree", {
                      required: true,
                      onChange: (event) => {
                        setState((prev) => ({ ...prev, submitButton: !event.target.checked }));
                      },
                    })}
                  />
                  {t("By checking this box, I agree to the")}{" "}
                  <Link to={`/${currentLang}/terms_and_conditions/`}>{t("Terms and Conditions")}</Link>{" "}
                  {t("and")}{" "}
                  <Link to={`/${currentLang}/privacy_policy/`}>{t("Privacy Policy")}</Link>.
                  </label>
                </div>
              </FormItem>

              <FormItem>
                <Button
                  variant="default"
                  width={100}
                  height={48}
                  disabled={submitButton}
                  text={t("Register")}
                />
              </FormItem>

              <div className="p-3 text-center pb-5">
                {t("Already have an account?")}{" "}
                <Link to={`/${currentLang}/login?appId=${idApp}`} className="fw-bold">
                  {t("Login")}
                </Link>
              </div>
            </form>
          </div>
        </>
      )}
    </>
  );
};

export default Registration;
